// The steps table shared by Settings → Pipeline steps and Settings → Workflow steps (Spencer, 2026-10-22; Iris's drawing
// docs/screens/steps-table-screens.html, the brief docs/steps-table-and-instructions-brief.md). One real table per stage:
// the header row's columns sit over their cells, grip and number at the left, every value a picker in its cell (one click
// opens the choice under the cell, nothing else moves), the name typed in place, saved on change with Undo on the row and
// no save button; Open at the row's right shows the step's detail below the row. Where the table cannot fit, the value
// columns drop to a labelled second line under the name and the row never piles up.

import { createPortal } from 'react-dom';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { refresh, rpc, useRows, type Row } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Edit, Info, usePopover } from '../../ui/kit';
import { Grip, type DragHandles } from '../../ui/move';

/** When, to the minute: Oct 19, 2:14 pm. */
export function stamp(v: unknown): string {
  if (!v) return '—';
  const d = new Date(String(v));
  if (isNaN(d.getTime())) return '—';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toLowerCase()}`;
}

/** A step's texts are written only through their database functions; the screens re-read what they touch. */
export async function stepCall(fn: 'step_change_file' | 'step_change_decide' | 'step_learned_edit', table: StepTableName, args: Row) {
  await rpc(fn, args);
  refresh(table, 'step_change', 'audit', 'standup_card');
}

// ---------- the width the table needs ----------

/** True while the element is at least `min` px wide: the table's own columns fit. */
export function useFits<T extends HTMLElement>(min: number) {
  const ref = useRef<T>(null);
  const [fits, setFits] = useState(() => window.innerWidth - 140 >= min);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setFits(el.clientWidth >= min));
    ro.observe(el);
    setFits(el.clientWidth >= min);
    return () => ro.disconnect();
  }, [min]);
  return { ref, fits };
}

// ---------- the in-cell picker ----------

export interface PickOption {
  key: string;
  label: string;
  /** The small words on the right (a seat's holder). */
  sub?: string;
  /** A heading the options after it sit under. */
  head?: string;
}

/** A value in its cell that opens its choices under the cell. The chosen value shows; nothing else moves. */
export function Picker({ value, sub, options, current, onPick, disabled, label, changed, muted, under, ghost }: { value: ReactNode; sub?: ReactNode; options: PickOption[]; current: string; onPick: (key: string) => void; disabled?: boolean; label: string; changed?: boolean; /** The default (No, none, every lead): drawn quieter, so a value that is set stands out. */ muted?: boolean; /** A line under the value: a proposed value in the suggestion accent, or the old value after a direct write (2026-10-24). */ under?: ReactNode; /** A proposed step's cell (a ghost row): dashed, in the suggestion accent. */ ghost?: boolean }) {
  const { canWrite } = useSession();
  const { open, toggle, close, btn, pop, at } = usePopover();
  const ro = disabled || !canWrite;
  if (ro)
    return (
      <span className={`spk ro${changed ? ' chg' : ''}${muted ? ' dim' : ''}${ghost ? ' ghost' : ''}`}>
        <span className="v">
          {value}
          {sub ? <small>{sub}</small> : null}
          {under}
        </span>
      </span>
    );
  return (
    <>
      <button ref={btn} type="button" className={`spk${open ? ' open' : ''}${changed ? ' chg' : ''}${muted ? ' dim' : ''}${ghost ? ' ghost' : ''}`} aria-haspopup="listbox" aria-expanded={open} aria-label={`${label}: change`} onClick={toggle}>
        <span className="v">
          {value}
          {sub ? <small>{sub}</small> : null}
          {under}
        </span>
        <span className="c" aria-hidden />
      </button>
      {open &&
        createPortal(
          <div ref={pop} role="listbox" aria-label={label} className="spmenu" style={{ top: at.top, left: at.left }}>
            {options.map((o) => (
              <div key={`${o.head ?? ''}${o.key}`} className="spgroup">
                {o.head && <div className="sphead">{o.head}</div>}
                <button
                  type="button"
                  role="option"
                  aria-selected={o.key === current}
                  className={o.key === current ? 'on' : ''}
                  onClick={(e) => {
                    e.stopPropagation();
                    close();
                    if (o.key !== current) onPick(o.key);
                  }}
                >
                  <span>{o.label}</span>
                  {o.sub && <small>{o.sub}</small>}
                </button>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------- Undo on the row ----------

export interface UndoNote {
  rowId: string;
  /** What changed, as it now reads (Seat → Designer); `was` is the whole change, old to new, for the hover. */
  words: string;
  was?: string;
  field: string;
  undo: () => Promise<unknown>;
}

/** The last change, shown on its row with Undo for a while; a newer change replaces it. */
export function useUndo(ms = 15000) {
  const [note, setNote] = useState<UndoNote | null>(null);
  useEffect(() => {
    if (!note) return;
    const t = window.setTimeout(() => setNote(null), ms);
    return () => window.clearTimeout(t);
  }, [note, ms]);
  return {
    note,
    record: (n: UndoNote) => setNote(n),
    clear: () => setNote(null),
  };
}

// ---------- the table ----------

export interface Col<R> {
  key: string;
  label: string;
  /** The i-dot text behind the label (the step_table_info setting). */
  info?: string;
  /** Its width at desk, px (the drawing's colgroup). */
  width: number;
  cell: (r: R) => ReactNode;
}

export interface StepTableProps<R extends { id: string }> {
  cols: Col<R>[];
  rows: R[];
  /** The number shown at the left: its place among the live rows, or a dash. */
  num: (r: R) => ReactNode;
  retired: (r: R) => boolean;
  /** The step column: the name typed in place and the small line under it. */
  name: (r: R) => ReactNode;
  open: string | null;
  setOpen: (id: string | null) => void;
  detail: (r: R) => ReactNode;
  undo: ReturnType<typeof useUndo>;
  drag: DragHandles;
  canEdit: boolean;
  /** Words on the row's action cell beside Open (who changed the row directly, when, and Undo). */
  flag?: (r: R) => ReactNode;
  foot?: ReactNode;
  /** A proposed step not yet approved (2026-10-24): drawn as a ghost row at its proposed position, no grip, no Open. */
  ghost?: (r: R) => boolean;
  /** The fixed-width Suggested column at the far right, shown only while something is pending on this table; the name column gives up the width. */
  tail?: { label: string; info?: string; width: number; cell: (r: R) => ReactNode };
  /** The row the address named, outlined for two seconds. */
  lit?: (r: R) => boolean;
}

/** The step table of one stage: a real table where its columns fit, the labelled two-line form where they do not. */
export function StepTable<R extends { id: string }>(p: StepTableProps<R>) {
  // while something is pending the far-right column widens to carry the Suggested cell (the drawing's last column) and the name
  // and value columns give up the width, so the table still fits at desk width
  const nameWidth = p.tail ? 238 : 272;
  const colWidth = (w: number) => (p.tail ? Math.round(w * 0.87) : w);
  const actWidth = p.tail ? p.tail.width : 90;
  const need = 26 + 30 + nameWidth + p.cols.reduce((s, c) => s + colWidth(c.width), 0) + actWidth;
  const { ref, fits } = useFits<HTMLDivElement>(need);
  const ncols = p.cols.length + 4;
  const rowClass = (r: R, base: string) => `${base}${p.open === r.id ? ' open' : ''}${p.retired(r) ? ' ret' : ''}${p.ghost?.(r) ? ' sghost' : ''}${p.lit?.(r) ? ' lit' : ''}`;
  const action = (r: R) => (
    <>
      {p.undo.note?.rowId === r.id && (
        <span className="sundo" title={p.undo.note.was}>
          <span>{p.undo.note.words}</span>
          <span>
            saved ·{' '}
          <button
            type="button"
            className="linklike"
            onClick={() => {
              const u = p.undo.note!;
              p.undo.clear();
              void u.undo();
            }}
          >
            Undo
          </button>
          </span>
        </span>
      )}
      {p.flag?.(r)}
      <button type="button" className={`sob${p.open === r.id ? ' on' : ''}`} aria-expanded={p.open === r.id} onClick={() => p.setOpen(p.open === r.id ? null : r.id)}>
        Open
        <span className="c" aria-hidden />
      </button>
    </>
  );
  // a click on the row's empty space opens it too
  const emptyClick = (r: R) => (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.closest('button, input, textarea, select, a, .spk, [draggable="true"]')) return;
    p.setOpen(p.open === r.id ? null : r.id);
  };
  return (
    <div ref={ref} className="stt-wrap">
      {fits ? (
        <table className="stt">
          <colgroup>
            <col style={{ width: 26 }} />
            <col style={{ width: 30 }} />
            <col style={{ width: nameWidth }} />
            {p.cols.map((c) => (
              <col key={c.key} style={{ width: colWidth(c.width) }} />
            ))}
            <col style={p.tail ? { width: actWidth } : undefined} />
          </colgroup>
          <thead>
            <tr>
              <th />
              <th />
              <th>Step</th>
              {p.cols.map((c) => (
                <th key={c.key}>
                  {c.label}
                  {c.info && <Info>{c.info}</Info>}
                </th>
              ))}
              <th className={p.tail ? 'stail' : undefined}>
                {p.tail?.label}
                {p.tail?.info && <Info left>{p.tail.info}</Info>}
              </th>
            </tr>
          </thead>
          <tbody>
            {p.rows.map((r) => {
              const isOpen = p.open === r.id;
              const ret = p.retired(r);
              const ghost = !!p.ghost?.(r);
              return (
                <RowPair key={r.id}>
                  <tr className={rowClass(r, 'srow')} data-step={r.id} {...(ghost ? {} : p.drag.row(r.id, rowClass(r, 'srow')))} onClick={ghost ? undefined : emptyClick(r)}>
                    <td className="g">{!ret && !ghost && p.canEdit ? <Grip {...p.drag.grip(r.id)} /> : null}</td>
                    <td className="n">{p.num(r)}</td>
                    <td className="nm">{p.name(r)}</td>
                    {p.cols.map((c) => (
                      <td key={c.key} className={`c-${c.key}`}>
                        {c.cell(r)}
                      </td>
                    ))}
                    <td className={`act${p.tail ? ' stail' : ''}`}>
                      {ghost ? null : action(r)}
                      {p.tail?.cell(r)}
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="sdet">
                      <td colSpan={ncols}>{p.detail(r)}</td>
                    </tr>
                  )}
                </RowPair>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div className="stn">
          {p.rows.map((r) => {
            const isOpen = p.open === r.id;
            const ret = p.retired(r);
            const ghost = !!p.ghost?.(r);
            const tail = p.tail?.cell(r);
            return (
              <RowPair key={r.id}>
                <div className={rowClass(r, 'snrow dragrow')} data-step={r.id} {...(ghost ? {} : p.drag.row(r.id, rowClass(r, 'snrow dragrow')))} onClick={ghost ? undefined : emptyClick(r)}>
                  <span className="g">{!ret && !ghost && p.canEdit ? <Grip {...p.drag.grip(r.id)} /> : null}</span>
                  <span className="n">{p.num(r)}</span>
                  <span className="nm">{p.name(r)}</span>
                  <span className="act">{ghost ? null : action(r)}</span>
                  <span className="vals">
                    {p.cols.map((c) => (
                      <span key={c.key} className={`nv c-${c.key}`}>
                        <span className="k">{c.label}</span>
                        {c.cell(r)}
                      </span>
                    ))}
                  </span>
                  {tail ? <span className="stail">{tail}</span> : null}
                </div>
                {isOpen && <div className="sdet">{p.detail(r)}</div>}
              </RowPair>
            );
          })}
        </div>
      )}
      {p.foot}
    </div>
  );
}

function RowPair({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

// ---------- the detail ----------

/** One field of the detail grid: its label (with an i dot) over its value. */
export function Field({ label, info, wide, children }: { label: string; info?: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={`sf${wide ? ' wide' : ''}`}>
      <span className="sl">
        {label}
        {info && <Info>{info}</Info>}
      </span>
      {children}
    </div>
  );
}

/** A read-only value in the detail grid (dashed box). */
export function Ro({ children }: { children: ReactNode }) {
  return <div className="sin ro">{children}</div>;
}

export type StepTableName = 'pipeline_step' | 'workflow_step';

/**
 * Instructions: how the step is done. A person edits the words here (a step change of kind instructions, which lands at once
 * for the owner or a person at Change; ug.step_change_file is the one way in); an AI login's suggestion waits under it, the
 * current and the proposed text side by side with the changed lines marked (2026-10-24), with Approve (as edited) and Send back.
 */
export function InstructionsField({ table, step, info, users }: { table: StepTableName; step: Row; info?: string; users: Row[] }) {
  const { canWrite } = useSession();
  const changes = useRows('step_change', { eq: { target_id: step.id as string, kind: 'instructions' }, order: ['-suggested_at'] });
  const waiting = changes.rows.find((c) => c.status === 'waiting');
  const last = changes.rows.find((c) => c.status !== 'waiting' && c.status !== 'replaced');
  const [edited, setEdited] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const who = (id: unknown) => users.find((u) => u.id === id)?.name ?? 'someone';
  const current = (step.instructions as string | null) ?? '';
  const proposedText = waiting ? ((waiting.proposed as Row | null)?.instructions as string | null) ?? '' : '';
  const stale = !!waiting && JSON.stringify(((waiting.before as Row | null)?.instructions as string | null) ?? null) !== JSON.stringify((step.instructions as string | null) ?? null);
  const decide = (approve: boolean) =>
    stepCall('step_change_decide', table, { p_change: waiting!.id, p_approve: approve, p_values: approve && edited !== null ? { instructions: edited || null } : null, p_note: note.trim() || null }).then(() => {
      setEdited(null);
      setNote('');
    });
  // the lines that differ, marked on each side
  const curLines = current.split('\n');
  const propLines = (edited ?? proposedText).split('\n');
  const curSet = new Set(curLines);
  const propSet = new Set(propLines);
  return (
    <Field label="Instructions" info={info} wide>
      {!waiting && (
        <Edit
          multiline
          className="sin ta"
          value={current}
          placeholder={canWrite ? 'How the step is done: the actions and reminders the seat follows' : 'None written yet'}
          onSave={(v) => stepCall('step_change_file', table, { p_table: table, p_kind: 'instructions', p_target: step.id, p_proposed: { instructions: v || null }, p_reason: 'edited on the step', p_stage: null })}
        />
      )}
      {waiting && (
        <div className={`ssug side${stale ? ' stale' : ''}`} data-change={waiting.id}>
          <div className="ssug-head">
            <b>{who(waiting.suggested_by)} suggests</b> <small>· {stamp(waiting.suggested_at)}{waiting.reason ? ` · “${waiting.reason}”` : ''}</small>
            {stale && <small className="ssg-stale">Step changed since this was filed</small>}
          </div>
          <div className="ssug-cols">
            <div>
              <small className="smeta">Now</small>
              <div className="sin ta ro sdiff">{curLines.map((l, i) => <span key={i} className={propSet.has(l) ? '' : 'gone'}>{l || ' '}</span>)}</div>
            </div>
            <div>
              <small className="smeta">Proposed</small>
              {canWrite && !stale ? (
                <Edit multiline className="sin ta" value={edited ?? proposedText} onSave={(v) => setEdited(v)} />
              ) : (
                <div className={`sin ta ro sdiff${stale ? ' sstale' : ''}`}>{propLines.map((l, i) => <span key={i} className={curSet.has(l) ? '' : 'new'}>{l || ' '}</span>)}</div>
              )}
            </div>
          </div>
          {canWrite && (
            <>
              <input className="sin ssg-note" value={note} placeholder={`A note to ${who(waiting.suggested_by)} (optional)`} onChange={(e) => setNote(e.target.value)} aria-label="A note on the decision" />
              <div className="ssug-act">
                {!stale && (
                  <button type="button" className="sob p" onClick={() => void decide(true)}>
                    Approve{edited !== null && edited !== proposedText ? ' as edited' : ' · new Instructions'}
                  </button>
                )}
                <button type="button" className="sob" onClick={() => void decide(false)}>
                  {note.trim() || stale ? `Send back to ${who(waiting.suggested_by)}` : 'Send back'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {!waiting && last && (
        <small className="smeta">
          Last suggestion: {who(last.suggested_by)}'s, {last.status === 'approved' ? 'approved' : last.status === 'applied' ? 'written' : last.status === 'undone' ? 'undone' : 'sent back'}{last.decided_by ? ` by ${who(last.decided_by)}` : ''} · {stamp(last.decided_at ?? last.suggested_at)}
        </small>
      )}
    </Field>
  );
}

interface Lesson {
  id: string;
  on: string;
  text: string;
  by: string | null;
}

/** Learned doing it: the dated lines the seat holder keeps (one per lesson). The owner may add, rewrite or remove too. */
export function LearnedField({ table, step, info, users }: { table: StepTableName; step: Row; info?: string; users: Row[] }) {
  const { canWrite } = useSession();
  const lines = (Array.isArray(step.learned) ? step.learned : []) as Lesson[];
  const [adding, setAdding] = useState('');
  const edit = (action: 'add' | 'rewrite' | 'remove', line: string | null, text: string | null) => stepCall('step_learned_edit', table, { p_table: table, p_step: step.id, p_action: action, p_line: line, p_text: text });
  return (
    <Field label="Learned doing it" info={info} wide>
      <div className="slearn">
        {lines.length === 0 && <small className="smeta">Nothing learned yet. The seat holder adds a line after a run or a live session.</small>}
        {lines.map((l) => (
          <div key={l.id} className="sline">
            <small className="when">
              {l.on} · {users.find((u) => u.id === l.by)?.name ?? '—'}
            </small>
            {canWrite ? <Edit className="sin" value={l.text} onSave={(v) => (v.trim() ? edit('rewrite', l.id, v) : edit('remove', l.id, null))} /> : <span>{l.text}</span>}
            {canWrite && (
              <button type="button" className="linklike" aria-label={`Remove: ${l.text}`} onClick={() => void edit('remove', l.id, null)}>
                ×
              </button>
            )}
          </div>
        ))}
        {canWrite && (
          <div className="sline add">
            <input
              className="sin"
              value={adding}
              placeholder="+ One lesson, one line"
              onChange={(e) => setAdding(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && adding.trim()) {
                  void edit('add', null, adding.trim()).then(() => setAdding(''));
                }
              }}
            />
          </div>
        )}
      </div>
    </Field>
  );
}

/** Who changed what and when: the newest change, and the list behind it (the audit). */
export function ChangesField({ table, step, users, words }: { table: StepTableName; step: Row; users: Row[]; words: (col: string, v: unknown) => string | null }) {
  const audit = useRows('audit', { eq: { table_name: table, row_id: step.id as string }, order: ['-changed_at'] });
  const [all, setAll] = useState(false);
  const who = (id: unknown) => (id ? users.find((u) => u.id === id)?.name ?? 'someone' : 'the seed');
  const SKIP = new Set(['updated_at', 'instructions_changed_at', 'level', 'created_at', 'created_by']);
  const entries = audit.rows
    .map((a) => {
      const b = (a.before ?? {}) as Row;
      const f = (a.after ?? {}) as Row;
      const cols = Object.keys(f).filter((k) => !SKIP.has(k) && JSON.stringify(b[k]) !== JSON.stringify(f[k]));
      const what = cols
        .map((k) => {
          const label = words(k, undefined);
          if (!label) return null;
          if (k === 'instructions' || k === 'learned' || k === 'note' || k === 'done_when') return `${label} changed`;
          return `${label}: ${words(k, b[k]) ?? '—'} → ${words(k, f[k]) ?? '—'}`;
        })
        .filter(Boolean);
      return what.length ? { id: a.id as number, at: a.changed_at as string, by: who(a.changed_by), what: what.join('; ') } : null;
    })
    .filter((x): x is { id: number; at: string; by: string; what: string } => !!x);
  const newest = entries[0];
  return (
    <Field label="Last changed">
      <Ro>{newest ? `${newest.by} · ${stamp(newest.at)}` : step.updated_at ? `as loaded · ${stamp(step.updated_at)}` : 'as loaded'}</Ro>
      {entries.length > 0 && (
        <button type="button" className="linklike smeta" onClick={() => setAll(!all)}>
          {all ? 'Hide the changes' : `Who changed what · ${entries.length}`}
        </button>
      )}
      {all && (
        <ul className="schanges">
          {entries.slice(0, 30).map((e) => (
            <li key={e.id}>
              <small>
                {stamp(e.at)} · {e.by}
              </small>{' '}
              {e.what}
            </li>
          ))}
        </ul>
      )}
    </Field>
  );
}

// ---------- the step named in the address ----------

/** ?step=<key> opens that step (a Standup card's link, the connector's approve_at), once its rows are in. */
export function useStepFromAddress(rows: { id: string; key: string }[], open: (id: string) => void) {
  const [params] = useSearchParams();
  const want = params.get('step');
  const done = useRef<string | null>(null);
  useEffect(() => {
    if (!want || done.current === want) return;
    const r = rows.find((x) => x.key === want);
    if (!r) return;
    done.current = want;
    open(r.id);
    // the stage's header in view with the step under it, so the list and the stage are named; a step far down its stage is centred instead
    window.setTimeout(() => {
      const row = document.querySelector(`[data-step="${r.id}"]`);
      const stage = row?.closest('.sstg');
      if (stage && row && row.getBoundingClientRect().top - stage.getBoundingClientRect().top < window.innerHeight * 0.45) stage.scrollIntoView({ block: 'start' });
      else row?.scrollIntoView({ block: 'center' });
    }, 200);
  }, [want, rows, open]);
}
